import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Checkbox,
  FormControlLabel,
  TablePagination,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { ownerApi } from '../../services/api';
import { Owner } from '../../types';

const OwnerList: React.FC = () => {
  const [owners, setOwners] = useState<Owner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOwner, setEditingOwner] = useState<Owner | null>(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form fields
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mailingAddress, setMailingAddress] = useState('');
  const [isCompany, setIsCompany] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchOwners = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await ownerApi.getAll();
      const data = response.data;
      setOwners(Array.isArray(data) ? data : data?.data || []);
    } catch (err: any) {
      setError('Failed to load owners. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  const openAddDialog = () => {
    setEditingOwner(null);
    setFullName('');
    setEmail('');
    setPhone('');
    setMailingAddress('');
    setIsCompany(false);
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (owner: Owner) => {
    setEditingOwner(owner);
    setFullName(owner.fullName);
    setEmail(owner.email);
    setPhone(owner.phone);
    setMailingAddress(owner.mailingAddress);
    setIsCompany(owner.isCompany);
    setFormError('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingOwner(null);
  };

  const handleSave = async () => {
    if (!fullName.trim()) {
      setFormError('Full Name is required.');
      return;
    }
    if (!email.trim()) {
      setFormError('Email is required.');
      return;
    }

    setSaving(true);
    setFormError('');

    try {
      const payload = {
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        mailingAddress: mailingAddress.trim(),
        isCompany,
      };

      if (editingOwner) {
        await ownerApi.update(editingOwner.id, payload);
      } else {
        await ownerApi.create(payload);
      }

      await fetchOwners();
      handleCloseDialog();
    } catch (err: any) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0] ||
        'Failed to save owner.';
      setFormError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const displayedOwners = owners.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Owners
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>
          Add Owner
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Full Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedOwners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No owners found. Add one to get started.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedOwners.map((owner) => (
                  <TableRow key={owner.id} hover>
                    <TableCell>{owner.fullName}</TableCell>
                    <TableCell>{owner.email}</TableCell>
                    <TableCell>{owner.phone}</TableCell>
                    <TableCell>
                      <Chip
                        label={owner.isCompany ? 'Company' : 'Individual'}
                        color={owner.isCompany ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => openEditDialog(owner)} color="primary">
                        <Edit />
                      </IconButton>
                      <IconButton size="small" color="error">
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={owners.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingOwner ? 'Edit Owner' : 'Add Owner'}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {formError}
            </Alert>
          )}
          <TextField
            label="Full Name"
            fullWidth
            margin="normal"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            disabled={saving}
          />
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={saving}
          />
          <TextField
            label="Phone"
            fullWidth
            margin="normal"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={saving}
          />
          <TextField
            label="Mailing Address"
            fullWidth
            margin="normal"
            value={mailingAddress}
            onChange={(e) => setMailingAddress(e.target.value)}
            multiline
            rows={2}
            disabled={saving}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isCompany}
                onChange={(e) => setIsCompany(e.target.checked)}
                disabled={saving}
              />
            }
            label="This is a company"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : undefined}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OwnerList;
